import { Wallet } from 'lucide-react'
import { Link } from 'react-router'
import { CategoryIcon } from '../../platform/icons/CategoryIcon'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { Money } from '../../platform/primitives/Money'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import type { Category } from '../categories/categoryTree'
import {
  BUDGET_FILTERS,
  filterCounts,
  envelopeSkin,
  isOver,
  matches,
  periodLabel,
  reason,
  type BudgetFilter,
} from './budgetRules'
import { budgetDetailPath } from './budgetPaths'
import { BUDGET_CURRENCY, type Envelope } from './budgetWire'
import { EnvelopeBar } from './EnvelopeBar'
import './budget.css'

/**
 * The Envelopes list (M4-R5, R6, R7). Every figure is the server's: the
 * remainder is `progress.remaining` exactly as sent (clamped at 0), an over row
 * shows the server's percentage instead of a money figure (B2), and the filter
 * predicates read the server's flags. Nothing here adds or subtracts money.
 */

type Props = {
  active: Envelope[]
  paused: Envelope[]
  /** null while loading: rows fall back to the generic icon. */
  categories: readonly Category[] | null
  filter: BudgetFilter
  onFilter: (f: BudgetFilter) => void
  /** Opens the edit sheet. */
  onResume: (e: Envelope) => void
}

const EMPTY_COPY: Record<BudgetFilter, string> = {
  all: 'No active envelopes',
  over: 'No envelopes are over',
  close: 'No envelopes are close to their limit',
  untouched: 'No envelopes are untouched',
}

function EnvelopeIcon({ e, categories }: { e: Envelope; categories: readonly Category[] | null }) {
  const category = e.categoryId === null ? undefined : categories?.find((c) => c.id === e.categoryId)
  return (
    <span className="env-row__icon">
      {category ? (
        <CategoryIcon category={category} />
      ) : (
        <span data-icon="all-spending">
          <Icon glyph={Wallet} />
        </span>
      )}
    </span>
  )
}

function Row({
  e,
  categories,
  onResume,
  paused,
  hit,
}: {
  e: Envelope
  categories: readonly Category[] | null
  onResume: (e: Envelope) => void
  paused: boolean
  hit: number
}) {
  const over = isOver(e)
  const sub = paused ? null : reason(e)
  const period = periodLabel(e.period)
  const state = paused ? 'paused' : envelopeSkin(e)
  return (
    <li className={`env-row env-row--${state}`}>
      <EnvelopeIcon e={e} categories={categories} />
      <div className="env-row__main">
        <Link to={budgetDetailPath(e.id)} className="env-row__name" style={{ minHeight: hit }}>
          {e.name}
        </Link>
        {(sub !== null || period !== null) && (
          <span className="env-row__sub">{[sub, period].filter(Boolean).join(' · ')}</span>
        )}
      </div>
      {!paused && <EnvelopeBar envelope={e} />}
      <span className="env-row__amounts">
        <Money cents={e.progress.spentCents} currency={BUDGET_CURRENCY} /> of{' '}
        <Money cents={e.amountCents} currency={BUDGET_CURRENCY} />
      </span>
      <span className="env-row__remain">
        {paused ? (
          <Button size="panel" variant="outline" onClick={() => onResume(e)} aria-label={`Resume ${e.name}`}>
            Resume
          </Button>
        ) : over ? (
          // The word, no number: the figure is the share USED (the subtitle carries it), and a
          // "+N%" beside it reads as N% beyond the allowance.
          <span className="env-over">
            Over budget
          </span>
        ) : (
          <>
            <Money cents={e.progress.remainingCents} currency={BUDGET_CURRENCY} /> left
          </>
        )}
      </span>
    </li>
  )
}

export function EnvelopeList({ active, paused, categories, filter, onFilter, onResume }: Props) {
  const phone = useBreakpoint() === 'phone'
  const hit = useHitTarget('page')
  const counts = filterCounts(active)
  const rows = active.filter((e) => matches(filter, e))
  return (
    <section className="env" aria-labelledby="env-heading">
      <div className="env__head">
        <h2 id="env-heading" className="env__title">
          Envelopes
        </h2>
        <div className={phone ? 'env__filter env__filter--phone' : 'env__filter'}>
          <SegmentedControl
            label="Filter envelopes"
            name="budget-filter"
            value={filter}
            onChange={onFilter}
            options={BUDGET_FILTERS.map((f) => ({ value: f.value, label: f.label, count: counts[f.value] }))}
          />
        </div>
      </div>
      <div className="wg-scroll">
      {rows.length === 0 ? (
        <div className="env__empty">
          <p>{EMPTY_COPY[filter]}</p>
          {filter !== 'all' && (
            <Button size="panel" variant="outline" onClick={() => onFilter('all')}>
              Show all
            </Button>
          )}
        </div>
      ) : (
        <ul className={phone ? 'env-list env-list--phone' : 'env-list'} aria-label="Envelopes">
          {rows.map((e) => (
            <Row key={e.id} e={e} categories={categories} onResume={onResume} paused={false} hit={hit} />
          ))}
        </ul>
      )}
      {paused.length > 0 && (
        <div className="env__paused">
          <h3 className="env__subtitle">
            Paused <span className="env__count">{paused.length}</span>
          </h3>
          <ul className={phone ? 'env-list env-list--phone' : 'env-list'} aria-label="Paused envelopes">
            {paused.map((e) => (
              <Row key={e.id} e={e} categories={categories} onResume={onResume} paused hit={hit} />
            ))}
          </ul>
        </div>
      )}
      </div>
    </section>
  )
}
